import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { LabelsQuery } from '@app/pages/settings/graphql/labels.queries';
import { ILabel } from '@app/pages/settings/interfaces/labels.interfaces';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import {
	CreateLabelMutation,
	DeleteLabelMutation,
	EditLabelMutation,
} from '@app/pages/settings/graphql/labels.mutations';
import { map } from 'rxjs/operators';
import { AuErrorAdapter } from '@app/shared';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';

export const defaultLabelColors = [
	'#FF993C',
	'#3B946F',
	'#1A4460',
	'#FF6A47',
	'#78838D',
	'#8DC173',
];

@Injectable({
	providedIn: 'root',
})
export class LabelsService {
	private readonly apollo = inject(Apollo);
	private readonly notify = inject(IconSnackBarService);

	labels: WritableSignal<ILabel[]> = signal([]);

	constructor() {
		this.getLabels();
	}

	getLabels() {
		this.apollo
			.query<{ labels: ILabel[] }>({
				query: LabelsQuery,
			})
			.subscribe(({ data }) => {
				this.labels.set([...data.labels]);
			});
	}

	createLabel(
		createLabelObj: Partial<ILabel>
	): Observable<{ label: ILabel; success: boolean }> {
		return this.apollo
			.mutate<{ createLabel: { label: ILabel; success: boolean } }>({
				mutation: CreateLabelMutation,
				variables: {
					...createLabelObj,
				},
			})
			.pipe(map(({ data }) => data.createLabel));
	}

	addLabel(label: ILabel) {
		this.labels.update(labels => [label, ...labels]);
	}

	updateLabel(label: ILabel) {
		this.labels.update(labels => {
			const index = labels.findIndex(l => l.pk === label.pk);
			labels[index] = label;
			return [...labels];
		});
	}

	editLabel(pk: string, label: Partial<ILabel>): Observable<{ label: ILabel }> {
		return this.apollo
			.mutate<{ editLabel: { label: ILabel } }>({
				mutation: EditLabelMutation,
				variables: {
					pk,
					...label,
				},
			})
			.pipe(map(({ data }) => data.editLabel));
	}

	deleteLabel(pk: string): void {
		this.apollo
			.mutate<{ deleteLabel: { success: boolean } }>({
				mutation: DeleteLabelMutation,
				variables: {
					pk,
				},
			})
			.pipe(map(({ data }) => data.deleteLabel))
			.subscribe({
				next: ({ success }) => {
					if (success) {
						this.labels.update(labels => labels.filter(label => label.pk !== pk));
					}
				},
				error: err => {
					this.notify.error(AuErrorAdapter.getTextFromGqlError(err));
				},
			});
	}
}
